import { setUrlSearchParam } from "@/lib/navigation";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { atomWithReset } from 'jotai/utils';


/**
 * Player Modal Stuff
 */
export type PlayerModalRefType = {
    mutex: string;
    netid: number;
} | {
    license: string;
} | {
    discord: string;
};
export const playerModalOpenAtom = atomWithReset(false);
export const playerModalRefAtom = atomWithReset<PlayerModalRefType | undefined>(undefined);
export const playerModalUrlParam = 'playerModal';

//Helper to set the URL search param
export const setPlayerModalUrlParam = (ref: string | undefined) => {
    setUrlSearchParam(playerModalUrlParam, ref);
}

//Hook to open the player modal
export const useOpenPlayerModal = () => {
    const setModalRef = useSetAtom(playerModalRefAtom);
    const setModalOpen = useSetAtom(playerModalOpenAtom);
    return (data: Exclude<PlayerModalRefType, undefined>) => {
        //Only write the param right away when the ref already is the preferred one.
        //Otherwise PlayerModal sets it once the player data resolved, so a netid or
        //license ref is never shown just to be swapped out a moment later.
        setPlayerModalUrlParam('discord' in data ? data.discord : undefined);
        setModalRef(data);
        setModalOpen(true);
    }
};

//Bumped when something outside the modal changed the player data, eg the action
//modal revoking or deleting one of their sanctions
export const playerModalRefreshKeyAtom = atom(0);

export const useRefreshPlayerModal = () => {
    const setRefreshKey = useSetAtom(playerModalRefreshKeyAtom);
    return () => setRefreshKey((current) => current + 1);
};

//Hook to close the player modal
export const useClosePlayerModal = () => {
    const setModalOpen = useSetAtom(playerModalOpenAtom);
    return () => {
        setPlayerModalUrlParam(undefined);
        setModalOpen(false);
    }
};


//General hook for the state of the modal
export const usePlayerModalStateValue = () => {
    const playerRef = useAtomValue(playerModalRefAtom);
    const [isModalOpen, setIsModalOpen] = useAtom(playerModalOpenAtom);
    return {
        isModalOpen,
        playerRef,
        closeModal: () => {
            setPlayerModalUrlParam(undefined);
            setIsModalOpen(false);
        },
    }
}
